import type { SVGProps } from 'react'

type P = SVGProps<SVGSVGElement> & { size?: number }

function Svg({ size = 18, children, ...rest }: P & { children: React.ReactNode }) {
  return (
    <svg
      // rem, so icons follow Settings → Text size (16 = 1rem at the default size)
      width={`${size / 16}rem`}
      height={`${size / 16}rem`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      // The look (SPEC §7.10): 1.5 strokes, square ends and corners.
      strokeWidth={1.5}
      strokeLinecap="square"
      strokeLinejoin="miter"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  )
}

export const IconPlus = (p: P) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
)
export const IconCheck = (p: P) => (
  <Svg {...p} strokeWidth={2.25}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </Svg>
)
export const IconChevronRight = (p: P) => (
  <Svg {...p}>
    <path d="M9 6l6 6-6 6" />
  </Svg>
)
export const IconChevronDown = (p: P) => (
  <Svg {...p}>
    <path d="M6 9l6 6 6-6" />
  </Svg>
)
/** Make bigger: two corners pulling out. */
export const IconExpand = (p: P) => (
  <Svg {...p}>
    <path d="M14 4h6v6M10 20H4v-6M20 4l-6.5 6.5M4 20l6.5-6.5" />
  </Svg>
)
/** Back to its size: two corners pushing in. */
export const IconCollapse = (p: P) => (
  <Svg {...p}>
    <path d="M20 10h-6V4M4 14h6v6M14 10l6.5-6.5M10 14l-6.5 6.5" />
  </Svg>
)
export const IconChevronLeft = (p: P) => (
  <Svg {...p}>
    <path d="M15 6l-6 6 6 6" />
  </Svg>
)
export const IconMore = (p: P) => (
  <Svg {...p}>
    <circle cx="5" cy="12" r="1.2" fill="currentColor" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    <circle cx="19" cy="12" r="1.2" fill="currentColor" />
  </Svg>
)
export const IconCalendar = (p: P) => (
  <Svg {...p}>
    <rect x="3.5" y="5" width="17" height="15" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </Svg>
)
export const IconRepeat = (p: P) => (
  <Svg {...p}>
    <path d="M17 2l3 3-3 3" />
    <path d="M4 11V9a4 4 0 014-4h12" />
    <path d="M7 22l-3-3 3-3" />
    <path d="M20 13v2a4 4 0 01-4 4H4" />
  </Svg>
)
export const IconUser = (p: P) => (
  <Svg {...p}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
  </Svg>
)
export const IconUsers = (p: P) => (
  <Svg {...p}>
    <circle cx="9" cy="8.5" r="3.5" />
    <path d="M2.5 20c0-3.4 2.9-5.5 6.5-5.5s6.5 2.1 6.5 5.5" />
    <path d="M15.5 5.2a3.5 3.5 0 0 1 0 6.6M17.5 14.8c2.4.6 4 2.4 4 5.2" />
  </Svg>
)
export const IconTag = (p: P) => (
  <Svg {...p}>
    <path d="M3 12V4a1 1 0 011-1h8l9 9-9 9-9-9z" />
    <circle cx="8" cy="8" r="1.4" fill="currentColor" />
  </Svg>
)
/** A card (Add → Card). */
export const IconCard = (p: P) => (
  <Svg {...p}>
    <rect x="4" y="5" width="16" height="14" />
    <path d="M8 10h8M8 14h5" />
  </Svg>
)
/** Completed items folded away (Marked as done → Collapse). */
export const IconFold = (p: P) => (
  <Svg {...p}>
    <path d="m7 20 5-5 5 5M7 4l5 5 5-5" />
  </Svg>
)
/** Down to the bottom (Marked as done → Move to bottom). */
export const IconToBottom = (p: P) => (
  <Svg {...p}>
    <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
  </Svg>
)
/** A detail: the same # that starts every detail capsule. */
export const IconHash = (p: P) => (
  <Svg {...p}>
    <path d="M9 4 7 20M17 4l-2 16M4.5 9h16M3.5 15h16" />
  </Svg>
)
export const IconSearch = (p: P) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </Svg>
)
export const IconChat = (p: P) => (
  <Svg {...p}>
    <path d="M4 5h16v11H11l-4.5 3.5V16H4z" />
  </Svg>
)
export const IconSend = (p: P) => (
  <Svg {...p}>
    <path d="M12 19V5M6 11l6-6 6 6" />
  </Svg>
)
export const IconBoard = (p: P) => (
  <Svg {...p}>
    <rect x="3.5" y="3.5" width="7" height="10" />
    <rect x="13.5" y="3.5" width="7" height="6" />
    <rect x="3.5" y="16.5" width="7" height="4" />
    <rect x="13.5" y="12.5" width="7" height="8" />
  </Svg>
)
export const IconDays = (p: P) => (
  <Svg {...p}>
    <rect x="3.5" y="5" width="17" height="15" />
    <path d="M3.5 10h17M8 3v4M16 3v4M8 14h2M14 14h2M8 17h2" />
  </Svg>
)
// A sticky note (square, its corner folded), so it doesn't read as Pages' sheets.
export const IconNotes = (p: P) => (
  <Svg {...p}>
    <path d="M4.5 4.5h15v10l-5 5h-10z" />
    <path d="M19.5 14.5h-5v5M8.5 9h7M8.5 12.5h4" />
  </Svg>
)
export const IconMenu = (p: P) => (
  <Svg {...p}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Svg>
)
export const IconX = (p: P) => (
  <Svg {...p}>
    <path d="M6 6l12 12M18 6L6 18" />
  </Svg>
)
export const IconTrash = (p: P) => (
  <Svg {...p}>
    <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
  </Svg>
)
export const IconArchive = (p: P) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="4" />
    <path d="M5 8v11a1 1 0 001 1h12a1 1 0 001-1V8M10 12h4" />
  </Svg>
)
export const IconPin = (p: P) => (
  <Svg {...p}>
    <path d="M9 4h6l-1 6 4 3v2H6v-2l4-3z" />
    <path d="M12 15v6" />
  </Svg>
)
export const IconShare = (p: P) => (
  <Svg {...p}>
    <path d="M12 3v12M7 8l5-5 5 5" />
    <path d="M5 13v6a2 2 0 002 2h10a2 2 0 002-2v-6" />
  </Svg>
)
export const IconLink = (p: P) => (
  <Svg {...p}>
    <path d="M10 14a4 4 0 005.66 0l3-3a4 4 0 00-5.66-5.66l-1 1" />
    <path d="M14 10a4 4 0 00-5.66 0l-3 3a4 4 0 005.66 5.66l1-1" />
  </Svg>
)
export const IconUnlink = (p: P) => (
  <Svg {...p}>
    <path d="M10 14a4 4 0 005.66 0l3-3a4 4 0 00-5.66-5.66l-1 1" />
    <path d="M14 10a4 4 0 00-5.66 0l-3 3a4 4 0 005.66 5.66l1-1" />
    <path d="M4 4l16 16" />
  </Svg>
)
export const IconImage = (p: P) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="16" />
    <circle cx="9" cy="10" r="1.8" />
    <path d="M21 16l-5-5-9 9" />
  </Svg>
)
export const IconPaperclip = (p: P) => (
  <Svg {...p}>
    <path d="M20 11.5l-8.5 8.5a5 5 0 01-7-7L13 4.5a3.5 3.5 0 015 5L9.5 18a2 2 0 01-3-3l8-8" />
  </Svg>
)
export const IconMapPin = (p: P) => (
  <Svg {...p}>
    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0112 2.5a7 7 0 017 7C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </Svg>
)
export const IconBell = (p: P) => (
  <Svg {...p}>
    <path d="M6 16V11a6 6 0 0112 0v5l1.5 2h-15z" />
    <path d="M10 20.5a2 2 0 004 0" />
  </Svg>
)
export const IconClock = (p: P) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </Svg>
)
export const IconGrip = (p: P) => (
  <Svg {...p}>
    <circle cx="9" cy="6" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="6" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="9" cy="12" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="12" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="9" cy="18" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="18" r="1.1" fill="currentColor" stroke="none" />
  </Svg>
)
export const IconInbox = (p: P) => (
  <Svg {...p}>
    <path d="M4 13l2.5-8h11L20 13v6a1 1 0 01-1 1H5a1 1 0 01-1-1z" />
    <path d="M4 13h4.5l1.5 2.5h4l1.5-2.5H20" />
  </Svg>
)
export const IconSparkle = (p: P) => (
  <Svg {...p}>
    <path d="M12 3.5l1.8 6.7 6.7 1.8-6.7 1.8L12 20.5l-1.8-6.7L3.5 12l6.7-1.8z" />
  </Svg>
)
export const IconCamera = (p: P) => (
  <Svg {...p}>
    <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
    <circle cx="12" cy="13" r="3.5" />
  </Svg>
)
export const IconArrowUpRight = (p: P) => (
  <Svg {...p}>
    <path d="M7 17L17 7M8 7h9v9" />
  </Svg>
)
export const IconArrowLeft = (p: P) => (
  <Svg {...p}>
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </Svg>
)
export const IconArrowRight = (p: P) => (
  <Svg {...p}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Svg>
)
export const IconCopy = (p: P) => (
  <Svg {...p}>
    <rect x="8" y="8" width="12" height="12" />
    <path d="M16 8V5a1 1 0 00-1-1H5a1 1 0 00-1 1v10a1 1 0 001 1h3" />
  </Svg>
)
export const IconMove = (p: P) => (
  <Svg {...p}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Svg>
)
export const IconDot = (p: P) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
  </Svg>
)
export const IconUndo = (p: P) => (
  <Svg {...p}>
    <path d="M9 14L4 9l5-5" />
    <path d="M4 9h10.5a5.5 5.5 0 010 11H11" />
  </Svg>
)
export const IconFilter = (p: P) => (
  <Svg {...p}>
    <path d="M4 6.5h16M7 12h10M10 17.5h4" />
  </Svg>
)
export const IconRedo = (p: P) => (
  <Svg {...p}>
    <path d="M15 14l5-5-5-5" />
    <path d="M20 9H9.5a5.5 5.5 0 000 11H13" />
  </Svg>
)
export const IconSettings = (p: P) => (
  <Svg {...p}>
    <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
    <rect x="13" y="5" width="4" height="4" />
    <rect x="7" y="15" width="4" height="4" />
  </Svg>
)
export const IconFlag = (p: P) => (
  <Svg {...p}>
    <path d="M5 21V4M5 4h11l-2 4 2 4H5" />
  </Svg>
)
export const IconEye = (p: P) => (
  <Svg {...p}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
)
export const IconPage = (p: P) => (
  <Svg {...p}>
    <path d="M7 3.5h7l4 4V20a.5.5 0 0 1-.5.5h-10.5a.5.5 0 0 1-.5-.5V4a.5.5 0 0 1 .5-.5z" />
    <path d="M14 3.5V8h4" />
  </Svg>
)
export const IconSmile = (p: P) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.5 14c.8 1.2 2 1.8 3.5 1.8s2.7-.6 3.5-1.8" />
    <path d="M9.25 9.75h.01M14.75 9.75h.01" strokeWidth={2.4} />
  </Svg>
)
export const IconPages = (p: P) => (
  <Svg {...p}>
    <path d="M8.5 3.5h6.5l4 4v10h-10.5z" />
    <path d="M5.5 7.5v13h9.5" />
  </Svg>
)
export const IconOutdent = (p: P) => (
  <Svg {...p}>
    <path d="M20 6H10M20 12h-7M20 18H10M7 9l-3 3 3 3" />
  </Svg>
)
export const IconIndent = (p: P) => (
  <Svg {...p}>
    <path d="M20 6H10M20 12h-7M20 18H10M4 9l3 3-3 3" />
  </Svg>
)
export const IconCircle = (p: P) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="7.5" />
  </Svg>
)
export const IconTask = (p: P) => (
  <Svg {...p}>
    <rect x="4.5" y="4.5" width="15" height="15" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </Svg>
)
/** A label: a line with rows tucked under it (no checkbox). */
export const IconLabel = (p: P) => (
  <Svg {...p}>
    <path d="M4 6.5h16M9 12h11M9 17.5h11" />
  </Svg>
)
export const IconPhone = (p: P) => (
  <Svg {...p}>
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2" />
  </Svg>
)
export const IconLock = (p: P) => (
  <Svg {...p}>
    <rect x="5" y="10.5" width="14" height="10" />
    <path d="M8.5 10.5V8a3.5 3.5 0 017 0v2.5" />
  </Svg>
)
export const IconAZ = (p: P) => (
  <Svg {...p}>
    <path d="M3.5 18L7 6l3.5 12M4.7 14h4.6M14 6h6l-6 12h6" />
  </Svg>
)
/** Charts: a pie with its slice pulled out a little. */
/** The eye, hidden: what comes in from elsewhere (charts, Google calendars), not shown. */
export const IconEyeOff = (p: P) => (
  <Svg {...p}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
    <path d="M4 20 20 4" />
  </Svg>
)

/** Days as a month. */
export const IconMonth = (p: P) => (
  <Svg {...p}>
    <path d="M3.5 5.5h17v14h-17zM3.5 10h17M3.5 14.75h17M9.2 10v9.5M14.8 10v9.5" />
  </Svg>
)

export const IconChart = (p: P) => (
  <Svg {...p}>
    <path d="M12 3a9 9 0 1 0 9 9h-9z" />
    <path d="M15 3.5A9 9 0 0 1 20.5 9H15z" />
  </Svg>
)
export const IconPencil = (p: P) => (
  <Svg {...p}>
    <path d="M4 20h4L19 9l-4-4L4 16z" />
  </Svg>
)
export const IconRotate = (p: P) => (
  <Svg {...p}>
    <path d="M19 12a7 7 0 1 1-2.1-5" />
    <path d="M19 4v4h-4" />
  </Svg>
)
/** Diagram tools (SPEC §7.11). */
export const IconPointer = (p: P) => (
  <Svg {...p}>
    <path d="M6 4l12 7-5 2-2 5z" />
  </Svg>
)
export const IconBox = (p: P) => (
  <Svg {...p}>
    <rect x="4" y="7" width="16" height="10" />
  </Svg>
)
export const IconDiamond = (p: P) => (
  <Svg {...p}>
    <path d="M12 4l8 8-8 8-8-8z" />
  </Svg>
)
export const IconLine = (p: P) => (
  <Svg {...p}>
    <path d="M5 19L19 5" />
  </Svg>
)
export const IconText = (p: P) => (
  <Svg {...p}>
    <path d="M6 6h12M12 6v13" />
  </Svg>
)
export const IconMeasure = (p: P) => (
  <Svg {...p}>
    <path d="M4 12h16M4 8v8M20 8v8" />
  </Svg>
)
export const IconDiagram = (p: P) => (
  <Svg {...p}>
    <rect x="3" y="7" width="11" height="11" />
    <circle cx="17.5" cy="7.5" r="3.5" />
    <path d="M3 21h11M3 20v2M14 20v2" />
  </Svg>
)
export const IconOutline = (p: P) => (
  <Svg {...p}>
    <path d="M5 8l6-4 8 5-2 10H7z" />
  </Svg>
)
